import React, { useEffect, useMemo, useState } from 'react';
import { Plus, TriangleAlert, X } from 'lucide-react';
import { v4 } from 'uuid';
import {
  Assert,
  formatTemplateToken,
  parseToolCallParamBindingRef,
  WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID,
} from '@cotera/utilities';
import { BroadcastEventType } from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@cotera/client/v0/components/ui/tabs';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@cotera/client/v0/components/ui/command';
import { useAction } from '@cotera/client/v0/actions/framework';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  useDatasetShape,
  type DatasetShape,
} from '@cotera/client/app/v1/trigger/hooks';
import { SetDatasetRowKeysAction } from '@cotera/client/app/v1/dataset/set-dataset-row-keys.action';
import { DatasetAddColumnForm } from '@cotera/client/app/v1/dataset/dataset-manual-controls';
import type { DatasetOperationColumnType } from '@cotera/client/app/v1/dataset/dataset-columns';
import { useWorkflowDatasetTrigger } from '../../../../hooks/use-workflow-dataset-trigger';
import {
  decodeToken,
  displayDottedPathFromRef,
  sourceLabelOfRef,
  sourceLabelsFromGroups,
  type UpstreamSourceGroup,
} from '../variable-picker-data';
import { TokenInput } from '../tiptap/token-input';
import type { ToolInputRefIssue } from '../tool-input-ref-issues';
import { RefIssueNotice } from '../ref-issue-notice';
import type { ToolInputControlProps } from './index';
import {
  deriveMode,
  parseRowKey,
  parseValues,
  readKeyColumns,
  serializeValues,
  type DraftValues,
  type WriteMode,
} from './dataset-rows-model';

/**
 * The triggering row's field with this name.
 *
 * Deliberately the trigger and not merely the nearest source: an intervening
 * agent step may well emit its own `name`, and binding the key to that would
 * update whichever row the agent happened to describe rather than the one the
 * run is about.
 */
const triggerRefForPath = (
  sources: readonly UpstreamSourceGroup[],
  path: readonly string[]
): string | null =>
  sources
    .find((source) => source.sourceName === WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID)
    ?.fields.find((candidate) => refPointerMatches(candidate.ref, path))?.ref ??
  null;

/**
 * Whether a ref addresses exactly this path.
 *
 * Compared as whole decoded segments rather than on the leaf name, which is
 * what this used to do: a dataset column called `rowKey` produces both
 * `/rowKey` and `/__meta/rowKey`, and matching on the leaf alone would pick
 * whichever the suggestion list happened to offer first.
 */
const refPointerMatches = (ref: string, path: readonly string[]): boolean => {
  const segments = parseToolCallParamBindingRef(ref)
    .pointer.split('/')
    .filter((segment) => segment !== '')
    .map(decodeToken);
  return (
    segments.length === path.length &&
    segments.every((segment, index) => segment === path[index])
  );
};

/**
 * Where a dataset trigger reports the changed row's identity — see
 * `buildDatasetRunInput`, which puts it on every dataset run input.
 */
const TRIGGER_ROW_KEY_PATH = [
  PersistedWorkflow.DATASET_RUN_INPUT_META_KEY,
  'rowKey',
];

/** Where that payload carries the row's own columns: at the top level. */
const triggerRefForColumn = (
  sources: readonly UpstreamSourceGroup[],
  column: string
): string | null => triggerRefForPath(sources, [column]);

/**
 * How the rows this node writes into will be found.
 *
 * Three ways of succeeding and two of failing, because a dataset can be keyed
 * without being keyed by anything a user could type:
 *
 * - `key` — declared business columns. Values identify the row, and they mean
 *   the same row before and after a re-upload.
 * - `row-id` — the dataset's key is the internal row id, and this node is fired
 *   by that dataset, so the run already carries the id of the row that changed.
 *   Exact within a run, which is all this needs to be.
 * - `internal` — the same internal key, with no triggering row to take it from.
 *   Nothing here can name a row, and matching on the row number would be a
 *   claim that outlives the next upload without surviving it.
 * - `none` — no key at all. A legacy artifact whose rows were never stamped;
 *   the dataset cannot be written row-wise however it is addressed.
 * - `unknown` — the dataset's shape has not arrived yet, so none of the above
 *   is known. Kept apart from `none` because they read identically off a null
 *   shape and do not mean remotely the same thing: reporting "this dataset has
 *   no row keys" for the moment before the fetch lands flashed the warning, and
 *   its offered fix, on every open of a perfectly well-keyed dataset.
 */
type RowIdentity =
  | { readonly t: 'key'; readonly columns: readonly string[] }
  | { readonly t: 'row-id'; readonly ref: string }
  | { readonly t: 'internal' }
  | { readonly t: 'none' }
  | { readonly t: 'unknown' };

const rowIdentityOf = (params: {
  readonly shape: DatasetShape | null;
  /** The triggering row's own id, when this node is fired by this dataset. */
  readonly rowIdRef: string | null;
}): RowIdentity => {
  const { shape, rowIdRef } = params;
  if (shape === null) {
    return { t: 'unknown' };
  }
  if (shape.keyColumns.length === 0) {
    return { t: 'none' };
  }
  if (!shape.keyIsInternal) {
    return { t: 'key', columns: shape.keyColumns };
  }
  return rowIdRef === null ? { t: 'internal' } : { t: 'row-id', ref: rowIdRef };
};

/**
 * What the committed row is addressed by: the columns to match on, a template
 * producing the row's declared key, or neither for an append.
 *
 * Neither for an *update* is not a write this tool can make — see `commit`,
 * which emits no rows at all rather than one that reads as an append.
 */
const rowAddress = (params: {
  readonly mode: WriteMode;
  readonly identity: RowIdentity;
  /** What the node already says, for the cases that cannot improve on it. */
  readonly saved: {
    readonly keyColumns: readonly string[];
    readonly rowKey: string;
  };
}): { keyColumns: string[] | null; rowKey: string | null } => {
  const { mode, identity, saved } = params;
  if (mode !== 'update') {
    return { keyColumns: null, rowKey: null };
  }
  switch (identity.t) {
    case 'key':
      return { keyColumns: [...identity.columns], rowKey: null };
    case 'row-id':
      return { keyColumns: null, rowKey: formatTemplateToken(identity.ref) };
    case 'unknown':
      // Nothing knows what identifies a row until the shape lands, so what is
      // saved stands. Recomputing from an unknown identity would drop a key the
      // node has had all along because someone typed during the fetch.
      return {
        keyColumns: saved.keyColumns.length > 0 ? [...saved.keyColumns] : null,
        rowKey: saved.rowKey === '' ? null : saved.rowKey,
      };
    case 'internal':
    case 'none':
      return { keyColumns: null, rowKey: null };
    default:
      return Assert.unreachable(identity);
  }
};

/**
 * What the editor says about how the row being written is found.
 *
 * Reported rather than asked — see {@link DatasetWriteControl}. Only the two
 * cases where nothing can name a row are a warning; the rest state the answer
 * the dataset already gave.
 */
const RowMatchNotice: React.FC<{
  readonly identity: RowIdentity;
  /** True when the key columns' values come from the triggering row. */
  readonly autoFilled: boolean;
  readonly onSetRowKeys: () => void;
}> = ({ identity, autoFilled, onSetRowKeys }) => {
  switch (identity.t) {
    case 'unknown':
      return (
        <Text.Caption className="text-muted-foreground">
          Checking how this dataset identifies a row…
        </Text.Caption>
      );
    case 'key':
      return (
        <Text.Caption className="text-muted-foreground">
          Matched on{' '}
          <span className="text-foreground font-medium">
            {identity.columns.join(' + ')}
          </span>
          {autoFilled
            ? ' — taken from the row that triggered the run.'
            : ' — give those columns the values that identify the row.'}
        </Text.Caption>
      );
    case 'row-id':
      // No warning: within a run this is exact, and the id comes from the same
      // trigger that started it. What the row number costs is a re-upload
      // question, and the trigger asks it first.
      return (
        <Text.Caption className="text-muted-foreground">
          Writes into{' '}
          <span className="text-foreground font-medium">
            the row that triggered the run
          </span>
          {' — this dataset has no business key, so the row is addressed by ' +
            'the id its trigger reported.'}
        </Text.Caption>
      );
    case 'internal':
    case 'none':
      return (
        <div className="border-destructive/40 bg-destructive/5 flex items-start gap-2 rounded-md border p-3">
          <TriangleAlert className="text-destructive mt-0.5 size-4 shrink-0" />
          <div className="flex min-w-0 flex-col items-start gap-2">
            <Text.Caption className="text-muted-foreground">
              {identity.t === 'internal'
                ? // Saying "no row keys" here was wrong twice over: the dataset
                  // has one, and the Set row keys dialog opens with it already
                  // ticked — so the offered fix looked like it had already been
                  // applied.
                  'This dataset is keyed by its row number, which every ' +
                  're-upload reassigns, and no trigger here reports one to ' +
                  'match on. Choose the column(s) that identify a row for good.'
                : 'This dataset has no row keys, so there is nothing to ' +
                  'match a row on. Choose the column(s) that identify a row — ' +
                  'the same key its triggers use.'}
            </Text.Caption>
            <Button variant="outline" size="sm" onClick={onSetRowKeys}>
              Set row keys
            </Button>
          </div>
        </div>
      );
    default:
      return Assert.unreachable(identity);
  }
};

/**
 * Which box a bad ref sits in.
 *
 * Issues are reported against a path into the saved param — `rows.0.values.email`
 * for a cell, `rows.0.rowKey` for the row key (see `serializeValues`) — and this is
 * what turns one back into the control that drew it. Matched across every row
 * index rather than only the first this editor shows, and on the whole tail
 * after `values.` rather than on a split, so a column whose name contains a dot
 * still finds its own box.
 */
const columnOfIssue = (issue: ToolInputRefIssue): string | null =>
  /^rows\.\d+\.values\.(.+)$/.exec(issue.path)?.[1] ?? null;

// Matches the identifier under either name: the control now writes it to
// `rowKey`, but a node not yet migrated off `rowId` still reports there.
const isRowIdIssue = (issue: ToolInputRefIssue): boolean =>
  /^rows\.\d+\.(rowKey|rowId)$/.test(issue.path);

/**
 * Declares a column on the dataset this write targets.
 *
 * The operation appended is the same `add_column` the dataset grid writes, so
 * both agree on what a column is and this edit lands in the same log as any
 * other. Shared by the column menu and by the notice on a value whose column
 * does not exist, which are the two ways this editor creates one.
 */
const useAddDatasetColumn = (
  datasetId: string,
  shape: DatasetShape
): ((input: {
  name: string;
  type: DatasetOperationColumnType;
}) => Promise<void>) => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  return async (input) => {
    const result = await client.resource.dataset.appendOperations({
      id: datasetId,
      // The artifact whose columns this drawer is showing. Declaring it is what
      // lets the server tell an edit written against the current schema from
      // one written against a schema that has since been replaced.
      baseArtifactId: shape.artifactId,
      operations: [
        {
          clientOperationId: v4(),
          op: {
            type: 'add_column',
            columnName: input.name,
            columnType: input.type,
          },
        },
      ],
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Could not add column'
          : 'Could not add column';
      toast.error(message);
      // Thrown rather than returned: the form only closes on a resolved submit,
      // so a rejected one keeps the name that was typed instead of discarding
      // it along with whatever the server objected to.
      throw new Error(message);
    }
    // Broadcast locally as well as by the server: the shape watchable is what
    // has to hear about the column for it to appear in the list below, and the
    // local event gets it there without waiting on the round trip.
    emit(BroadcastEventType.ResourceUpdated, {
      orgId,
      resourceType: 'dataset',
      resourceId: datasetId,
    });
    toast.success(`Added column ${input.name}`);
  };
};

/**
 * The one way into this write's Values: name a column of the dataset, or, when
 * the column wanted is not there, declare it on the dataset from the same menu.
 *
 * Both in one control because they answer the same question. Offered as two
 * buttons they read as two features, and stacked under a section that already
 * ends in the editor's own "Add input" they made three plus-buttons in a column
 * — three things to choose between where there is really one, and creating a
 * column is the rarer half of it.
 *
 * Creating is worth having here at all because a write can only name columns
 * that already exist — the tool refuses the rest, and the editor has no box to
 * type them into — so wanting somewhere to put a value the workflow produces
 * otherwise meant leaving the half-built node, opening the dataset, adding the
 * column there and coming back.
 */
const AddValueColumn: React.FC<{
  readonly datasetId: string;
  readonly shape: DatasetShape;
  /** The dataset's columns this write is not already asking about. */
  readonly columns: readonly string[];
  readonly onPick: (column: string) => void;
}> = ({ datasetId, shape, columns, onPick }) => {
  const addColumn = useAddDatasetColumn(datasetId, shape);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  /** So the form can refuse a name the dataset already has before the server does. */
  const takenNames = new Set(shape.columns);

  if (adding) {
    return (
      <DatasetAddColumnForm
        takenNames={takenNames}
        onSubmit={async (input) => {
          await addColumn(input);
          setAdding(false);
          // A column is created here in order to be written, so it arrives in
          // the section asking for its value rather than back in the menu.
          onPick(input.name);
        }}
        onCancel={() => setAdding(false)}
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Quieter than the editor's own "Add input" below it. Both add a
            field to this node, so weight is what says which one belongs to
            this section and which to the panel; two outline buttons in a
            column read as a choice between equals. */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 self-start"
        >
          <Plus className="mr-1 size-3.5" />
          Add a column
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-0">
        <Command>
          <CommandInput placeholder="Search this dataset's columns…" />
          <CommandList>
            {/* Not "no results": the way out of an empty search is the item
                below it, which is what someone typing a column the dataset
                does not have is looking for. */}
            <CommandEmpty>No column by that name.</CommandEmpty>
            {columns.length > 0 ? (
              <CommandGroup>
                {columns.map((column) => (
                  <CommandItem
                    key={column}
                    value={column}
                    onSelect={() => {
                      onPick(column);
                      setOpen(false);
                    }}
                  >
                    <span className="truncate">{column}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            <CommandGroup>
              <CommandItem
                value="__create__ new column"
                onSelect={() => {
                  setOpen(false);
                  setAdding(true);
                }}
              >
                <Plus className="mr-2 size-3.5" />
                <span className="truncate">New column on this dataset…</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Says that a value on this write names a column its dataset does not have.
 *
 * Such a value cannot be typed here — the menu only offers real columns — but
 * one written elsewhere (by Coco, or kept from before a column was dropped)
 * used to vanish from the drawer while staying on the node. The write then
 * failed at run time on a column nobody could see, and an edit that set it
 * looked like it had done nothing. Shown instead, with the two ways out: make
 * the column, or stop writing the value.
 */
const MissingColumnNotice: React.FC<{
  readonly datasetId: string;
  readonly shape: DatasetShape;
  readonly column: string;
}> = ({ datasetId, shape, column }) => {
  const addColumn = useAddDatasetColumn(datasetId, shape);
  const [creating, setCreating] = useState(false);

  if (creating) {
    // Arrives as an ordinary column once the dataset's shape hears about it,
    // with the value it already had: nothing else on the node has to change.
    return (
      <DatasetAddColumnForm
        takenNames={new Set(shape.columns)}
        initialName={column}
        onSubmit={async (input) => {
          await addColumn(input);
          setCreating(false);
        }}
        onCancel={() => setCreating(false)}
      />
    );
  }

  return (
    <div className="border-destructive/40 bg-destructive/5 flex items-start gap-2 rounded-md border p-3">
      <TriangleAlert className="text-destructive mt-0.5 size-4 shrink-0" />
      <div className="flex min-w-0 flex-col items-start gap-2">
        <Text.Caption className="text-muted-foreground">
          This dataset has no{' '}
          <span className="text-foreground font-medium">{column}</span> column,
          so the write will fail when it runs. Create the column, or stop
          writing this value.
        </Text.Caption>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setCreating(true)}
        >
          Create column
        </Button>
      </div>
    </div>
  );
};

/**
 * What this control draws when its `rows` are bound whole to one upstream ref
 * instead of mapped column by column here.
 *
 * This is the sanctioned shape for an agent purpose-built to emit the write's
 * `{ rowKey, values }` row array: it hands the write its rows with their types
 * intact, which per-column `{{...}}` tokens cannot do. The mapping then lives in
 * that step's output, not on this node — so there is nothing per-column to edit
 * here, and before this notice existed the section rendered blank, which read as
 * a broken node. Says where the rows come from instead, so the node is legible
 * and obviously intentional. Read-only: the binding is changed on the canvas by
 * reconnecting the edge, the same place it was made.
 */
const BoundRowsNotice: React.FC<{
  readonly sourceLabel: string | null;
  readonly fieldLabel: string;
}> = ({ sourceLabel, fieldLabel }) => (
  <div className="border-border bg-muted/40 flex flex-col gap-1.5 rounded-md border p-3">
    <Text.Caption className="text-foreground font-medium">
      Rows come from an earlier step
    </Text.Caption>
    <Text.Caption className="text-muted-foreground">
      {sourceLabel === null ? (
        <>This step is fed its rows by an upstream step.</>
      ) : (
        <>
          This step is fed its rows by{' '}
          <span className="text-foreground font-medium">{sourceLabel}</span>{' '}
          (its <span className="font-mono">{fieldLabel}</span> output).
        </>
      )}{' '}
      Each row's columns are set by that step's output, so there is nothing to
      map here. To set columns individually in this editor instead, reconnect
      the rows on the canvas.
    </Text.Caption>
  </div>
);

/**
 * Configures a dataset write.
 *
 * Four decisions, in the order they depend on each other: which dataset, add or
 * update, how an updated row is found, and what goes in it. The generic editor
 * cannot express any of them — it sees a uuid and an array-of-structs, and
 * renders a text box and a JSON textarea.
 *
 * How a row is found is not asked as a question. The dataset already answers it:
 * its declared row keys are what identify a row, the same claim the Set row keys
 * dialog makes and the same one its triggers match on. So this reports the
 * answer, and when the dataset has not made that claim yet it says so and offers
 * the dialog rather than inventing a key here that nothing else would honour.
 *
 * The exception is a dataset keyed by the internal row id — every CSV one, until
 * someone declares business columns. There is no key to type, but a node fired
 * by that dataset does not need one: the run carries the id of the row that
 * changed, so the write is addressed by id instead. Refusing that case is what
 * left "Update a row" unusable on a CSV dataset, and a workflow that could only
 * be saved as "Add a row" appended a row per run.
 *
 * A write whose `rows` are bound whole to an upstream ref is the exception to
 * all of the above: there is no per-column mapping on this node to edit, so it
 * renders {@link BoundRowsNotice} instead and returns early.
 */
export const DatasetWriteControl: React.FC<ToolInputControlProps> = ({
  field,
  params,
  paramBindings,
  sources,
  refIssues,
  setValues,
}) => {
  const savedValues = useMemo(
    () => parseValues(params[field.name]),
    [params, field.name]
  );
  /**
   * Values for an update that cannot name its row yet — the dataset's shape has
   * not loaded, or it has no key to match on.
   *
   * Such an update saves no rows at all (see `commit`), so without this its
   * values vanished from the screen, and the update with them: nothing wrote
   * it once the row could be named, so the node read back as "Add a row" the
   * next time it opened. Clicking "Update a row" before the shape arrived was
   * enough. Held here, they are committed the moment the row can be named.
   */
  const [pendingValues, setPendingValues] = useState<DraftValues | null>(null);
  const values = pendingValues ?? savedValues;
  const savedRowKey = useMemo(
    () => parseRowKey(params[field.name]),
    [params, field.name]
  );
  const keyColumns = useMemo(() => readKeyColumns(params), [params]);
  /**
   * The toggle has to hold on the mode someone picked until the write itself
   * says which it is. Mode is otherwise read straight off the saved input, and
   * an empty node says neither: "Add a row" with nothing typed into it writes
   * no key and no values, and an update on a dataset with no key writes nothing
   * at all. Both read back as unconfigured, which would spring the toggle back
   * the moment it was clicked and make the control look broken.
   */
  const [pickedMode, setPickedMode] = useState<WriteMode | null>(null);
  const mode = pickedMode ?? deriveMode(keyColumns, savedRowKey, savedValues);
  /**
   * Columns put on screen by hand, on top of the ones the saved write already
   * names.
   *
   * Needed because a box is not the same thing as a value: clearing the text in
   * one drops the column from what is saved, and without this the box would
   * vanish from under the cursor that just emptied it. A column leaves the list
   * only when its row is removed, or when the drawer is next opened.
   */
  const [addedColumns, setAddedColumns] = useState<ReadonlySet<string>>(
    new Set()
  );

  const datasetId =
    typeof params['datasetId'] === 'string' ? params['datasetId'] : null;
  const shape = useDatasetShape(datasetId);
  const datasetTrigger = useWorkflowDatasetTrigger();
  const setRowKeys = useAction(SetDatasetRowKeysAction);

  /** True when the rows arriving are this dataset's own, so their fields match. */
  const firedByThisDataset = datasetTrigger?.datasetId === datasetId;

  const identity = rowIdentityOf({
    shape,
    // Only from this dataset's own trigger: another dataset's row id addresses
    // a row in that dataset, and here it would name whatever row happened to
    // carry the same number.
    rowIdRef: firedByThisDataset
      ? triggerRefForPath(sources, TRIGGER_ROW_KEY_PATH)
      : null,
  });

  /**
   * The key columns as one comparable string.
   *
   * Hoisted out of the dependency array below rather than written inline: a
   * dependency that is an expression cannot be statically checked, so the rule
   * that verifies the rest of the list gives up on it.
   */
  const identityColumnsKey =
    identity.t === 'key' ? identity.columns.join() : '';
  /** The same, for the branch that binds an id instead of columns. */
  const identityRowIdRef = identity.t === 'row-id' ? identity.ref : null;

  /**
   * The key columns' values, taken from the triggering row.
   *
   * Only when every key column is present on it: a partial match would leave
   * one field auto-filled and another blank with no obvious reason why, which
   * is harder to act on than filling none of them.
   */
  const autoKeyValues = useMemo<DraftValues | null>(() => {
    if (identity.t !== 'key' || !firedByThisDataset) {
      return null;
    }
    const entries = identity.columns.map(
      (column) => [column, triggerRefForColumn(sources, column)] as const
    );
    return entries.every(([, ref]) => ref !== null)
      ? Object.fromEntries(
          entries.map(([column, ref]) => [
            column,
            formatTemplateToken(ref as string),
          ])
        )
      : null;
    // identity is derived from shape each render; key on what it is made of.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity.t, identityColumnsKey, firedByThisDataset, sources]);

  const commit = (
    nextValues: DraftValues,
    nextMode: WriteMode,
    nextIdentity: RowIdentity
  ): void => {
    const { keyColumns: nextKeyColumns, rowKey: nextRowKey } = rowAddress({
      mode: nextMode,
      identity: nextIdentity,
      saved: { keyColumns, rowKey: savedRowKey },
    });
    // Update with nothing to identify the row is not a write this tool can
    // make: naming neither a key nor a row key reads as an append, which is the
    // opposite of what the toggle says. Emitting no rows keeps the node
    // unsaveable until the dataset has a key, rather than quietly doing the
    // other thing — which is how a workflow ended up appending a row per run.
    const unresolvable =
      nextMode === 'update' && nextKeyColumns === null && nextRowKey === null;
    setPendingValues(unresolvable ? nextValues : null);
    setValues({
      [field.name]: unresolvable ? [] : serializeValues(nextValues, nextRowKey),
      keyColumns: nextKeyColumns,
    });
  };

  const setColumn = (column: string, template: string): void => {
    setAddedColumns((prev) =>
      prev.has(column) ? prev : new Set(prev).add(column)
    );
    commit({ ...values, [column]: template }, mode, identity);
  };

  /** Stops writing this column: the box goes, and so does its saved value. */
  const removeColumn = (column: string): void => {
    setAddedColumns((prev) => {
      const next = new Set(prev);
      next.delete(column);
      return next;
    });
    commit({ ...values, [column]: '' }, mode, identity);
  };

  const changeMode = (next: WriteMode): void => {
    setPickedMode(next);
    commit(values, next, identity);
  };

  /**
   * Writes what identifies the row in rather than only displaying it.
   *
   * In an effect because it has to happen whenever the node is capable of it —
   * on open, and after the dataset's schema or the trigger's fields arrive —
   * not only when someone clicks the toggle. Saying "taken from the row that
   * triggered the run" while leaving the param empty is how this failed at run
   * time with the key column missing.
   */
  useEffect(() => {
    if (mode !== 'update') {
      return;
    }
    if (identityRowIdRef !== null) {
      const template = formatTemplateToken(identityRowIdRef);
      // Nothing to write means no row is serialized at all, key included, so
      // committing here would leave `savedRowKey` empty and re-run this effect
      // forever. The key lands with the first value the user fills in.
      if (
        savedRowKey === template ||
        serializeValues(values, template).length === 0
      ) {
        return;
      }
      commit(values, mode, identity);
      return;
    }
    if (autoKeyValues === null) {
      return;
    }
    const missing = Object.entries(autoKeyValues).filter(
      ([column]) => (values[column] ?? '') === ''
    );
    if (missing.length === 0) {
      return;
    }
    commit({ ...values, ...Object.fromEntries(missing) }, mode, identity);
    // `commit` closes over this render's values; the guards above make it
    // converge after one write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, autoKeyValues, values, identityRowIdRef, savedRowKey]);

  /**
   * Commits an update held in {@link pendingValues} once its row can be named:
   * when the dataset's shape arrives, or a key is set on it.
   */
  useEffect(() => {
    if (pendingValues === null || mode !== 'update') {
      return;
    }
    const address = rowAddress({
      mode,
      identity,
      saved: { keyColumns, rowKey: savedRowKey },
    });
    if (address.keyColumns === null && address.rowKey === null) {
      return;
    }
    commit(pendingValues, mode, identity);
    // identity is derived from shape each render; key on what it is made of.
    // `commit` clears `pendingValues`, which is what stops this re-running.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingValues, mode, identity.t, identityColumnsKey, identityRowIdRef]);

  // `rows` bound whole to one upstream ref: the mapping is that step's output,
  // not this node's `params.rows` (which is empty), so the per-column editor
  // below has nothing to draw. Reported before the dataset check — the binding
  // is the whole story of this node whether or not a dataset is also chosen.
  const rowsBinding = paramBindings[field.name];
  if (rowsBinding !== undefined && rowsBinding !== '') {
    return (
      <BoundRowsNotice
        sourceLabel={sourceLabelOfRef(
          rowsBinding,
          sourceLabelsFromGroups(sources)
        )}
        fieldLabel={displayDottedPathFromRef(rowsBinding)}
      />
    );
  }

  if (datasetId === null) {
    return (
      <Text.Caption className="text-muted-foreground">
        Choose a dataset first — its columns are what can be written.
      </Text.Caption>
    );
  }

  const columns = shape?.columns ?? [];
  /**
   * Values this write sets on columns the dataset does not have. Only known
   * once the shape lands — before that every column would look missing.
   */
  const missingColumns =
    shape === null
      ? []
      : Object.keys(values).filter(
          (column) => (values[column] ?? '') !== '' && !columns.includes(column)
        );
  const issuesByColumn = new Map<string, ToolInputRefIssue[]>();
  const rowIdIssues: ToolInputRefIssue[] = [];
  /**
   * Anything that names neither a column this write sets nor the row id — a
   * ref inside a row this editor does not show. Reported at the end rather than
   * swallowed: an issue with nowhere to sit still blocks the save, and a blocked
   * save with no message is the thing being fixed here.
   */
  const otherIssues: ToolInputRefIssue[] = [];
  for (const issue of refIssues) {
    const column = columnOfIssue(issue);
    if (
      column !== null &&
      (columns.includes(column) || missingColumns.includes(column))
    ) {
      const existing = issuesByColumn.get(column);
      if (existing === undefined) {
        issuesByColumn.set(column, [issue]);
      } else {
        existing.push(issue);
      }
      continue;
    }
    if (isRowIdIssue(issue)) {
      rowIdIssues.push(issue);
      continue;
    }
    otherIssues.push(issue);
  }

  /** A column the row is matched on, which is a question the write must answer. */
  const isKeyColumn = (column: string): boolean =>
    mode === 'update' &&
    identity.t === 'key' &&
    identity.columns.includes(column);

  /**
   * A key the trigger already answers is not a question. Its value is written
   * into the node all the same — it is just not asked for. Unless what it
   * answers with reads nothing, in which case it is very much a question, and
   * hiding the box would hide the reason the node will not save.
   */
  const isAnsweredKey = (column: string): boolean =>
    isKeyColumn(column) &&
    autoKeyValues !== null &&
    (issuesByColumn.get(column) ?? []).length === 0;

  // In the dataset's own column order, so adding one does not reshuffle the
  // boxes already on screen. The answered-key test comes first and wins: that
  // column does carry a value, written by the effect above, and asking about a
  // value nobody typed is the thing `isAnsweredKey` exists to prevent.
  const shownColumns = columns.filter((column) =>
    isAnsweredKey(column)
      ? false
      : isKeyColumn(column) ||
        (values[column] ?? '') !== '' ||
        addedColumns.has(column) ||
        (issuesByColumn.get(column) ?? []).length > 0
  );
  const offerableColumns = columns.filter(
    (column) => !shownColumns.includes(column) && !isAnsweredKey(column)
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Tabs
          value={mode}
          onValueChange={(next) => changeMode(next as WriteMode)}
        >
          {/* Full-width, but still the flex list the triggers are built for:
              as a grid their `flex-1` does nothing and the two sit at
              different widths from their labels. */}
          <TabsList className="w-full">
            <TabsTrigger value="update">Update a row</TabsTrigger>
            <TabsTrigger value="create">Add a row</TabsTrigger>
          </TabsList>
        </Tabs>
        <Text.Caption className="text-muted-foreground">
          {mode === 'update'
            ? 'Finds a row that already exists and writes into it.'
            : 'Appends a new row to the dataset.'}
        </Text.Caption>
      </div>

      {mode === 'update' ? (
        <div className="flex flex-col gap-1">
          <RowMatchNotice
            identity={identity}
            autoFilled={autoKeyValues !== null}
            onSetRowKeys={() => void setRowKeys.execute({ datasetId })}
          />
          {/* Directly under the claim it contradicts: the notice says the row
              is addressed by the id its trigger reported, and this is where
              that stops being true. */}
          <RefIssueNotice issues={rowIdIssues} />
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <Text.Caption className="text-foreground font-medium">
            Values
          </Text.Caption>
          {/* Said once, about the section, rather than repeated into every
              box's placeholder — where it was also wrong half the time, since
              an appended row has no current value to keep. */}
          <Text.Caption className="text-muted-foreground">
            {mode === 'update'
              ? 'Only the columns below are written. Every other column keeps its current value.'
              : 'Only the columns below are written. Every other column is left empty.'}
          </Text.Caption>
        </div>
        {shape === null ? (
          // Same reason the notice above waits: "no columns yet" is a claim
          // about the dataset, not about the fetch that has not landed.
          <Text.Caption className="text-muted-foreground">
            Loading this dataset's columns…
          </Text.Caption>
        ) : columns.length === 0 ? (
          // Still worth saying, even with the button underneath: a dataset with
          // no columns is a fact about it, not an empty render.
          <Text.Caption className="text-muted-foreground">
            This dataset has no columns yet.
          </Text.Caption>
        ) : shownColumns.length === 0 && missingColumns.length === 0 ? (
          <Text.Caption className="text-muted-foreground">
            No columns set yet.
          </Text.Caption>
        ) : (
          shownColumns.map((column) => {
            const isKey = isKeyColumn(column);
            const columnIssues = issuesByColumn.get(column) ?? [];
            return (
              <div key={column} className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Text.Caption className="text-muted-foreground truncate">
                    {column}
                    {isKey ? (
                      // Named so it is obvious why this one is not optional,
                      // and that filling it selects rather than overwrites.
                      <span className="text-foreground">
                        {' '}
                        · identifies the row
                      </span>
                    ) : null}
                  </Text.Caption>
                  {/* A key column has no remove: dropping what the row is
                      matched on does not stop writing a column, it stops the
                      write finding a row at all. */}
                  {isKey ? null : (
                    <button
                      type="button"
                      onClick={() => removeColumn(column)}
                      title="Stop writing this column"
                      aria-label={`Stop writing ${column}`}
                      className="text-muted-foreground hover:bg-muted hover:text-foreground -mr-1 flex size-6 shrink-0 items-center justify-center rounded transition-colors"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>
                {/* The same token input every other tool input uses, so a value
                    can be typed or pulled from an earlier step the same way. */}
                <TokenInput
                  value={values[column] ?? ''}
                  placeholder={
                    isKey ? 'Value that identifies the row' : undefined
                  }
                  sources={sources}
                  onChange={(template) => setColumn(column, template)}
                />
                <RefIssueNotice issues={columnIssues} />
              </div>
            );
          })
        )}
        {/* After the dataset's own columns, since they are not among them. */}
        {shape === null
          ? null
          : missingColumns.map((column) => (
              <div key={column} className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Text.Caption className="text-destructive truncate">
                    {column} · not a column on this dataset
                  </Text.Caption>
                  <button
                    type="button"
                    onClick={() => removeColumn(column)}
                    title="Stop writing this column"
                    aria-label={`Stop writing ${column}`}
                    className="text-muted-foreground hover:bg-muted hover:text-foreground -mr-1 flex size-6 shrink-0 items-center justify-center rounded transition-colors"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
                <TokenInput
                  value={values[column] ?? ''}
                  sources={sources}
                  onChange={(template) => setColumn(column, template)}
                />
                <MissingColumnNotice
                  datasetId={datasetId}
                  shape={shape}
                  column={column}
                />
                <RefIssueNotice issues={issuesByColumn.get(column) ?? []} />
              </div>
            ))}
        <RefIssueNotice issues={otherIssues} />
        {/* Below the values rather than beside the heading: it is the way out
            of "the column I want to write is not here", which is only obvious
            once the list has been read. Withheld until the columns land, so it
            cannot offer a name the dataset already has. */}
        {shape === null ? null : (
          <AddValueColumn
            datasetId={datasetId}
            shape={shape}
            columns={offerableColumns}
            onPick={(column) =>
              setAddedColumns((prev) => new Set(prev).add(column))
            }
          />
        )}
      </div>
    </div>
  );
};
